import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import ColumnCustomizer from '../components/ColumnCustomizer';
import EmptyState from '../components/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './PresetChecklists.css';

const PresetChecklists = () => {
  const { t } = useTranslation(['presets', 'tasks', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [presetChecklists, setPresetChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    items: []
  });
  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    items: true
  });
  const [sorting, setSorting] = useState({ column: null, direction: null });

  const checklistColumnLabels = useMemo(() => ({
    name: t('common:name'),
    items: t('presets:columnItems')
  }), [t]);

  useEffect(() => {
    fetchPresetChecklists();
  }, [user]);

  const handleSort = (column) => {
    setSorting(prev => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { column: null, direction: null };
        }
      }
      return { column, direction: 'asc' };
    });
  };

  const renderSortableHeader = (column, label) => {
    const isSorted = sorting.column === column;
    const sortDirection = isSorted ? sorting.direction : null;
    
    return (
      <th 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => handleSort(column)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_upward</span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_downward</span>
          )}
        </div>
      </th>
    );
  };

  const sortChecklists = (checklistList) => {
    if (!sorting.column || !sorting.direction) {
      return checklistList;
    }

    return [...checklistList].sort((a, b) => {
      let aValue, bValue;

      switch (sorting.column) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'items':
          const aItems = Array.isArray(a.items) ? a.items.length : 0;
          const bItems = Array.isArray(b.items) ? b.items.length : 0;
          aValue = aItems;
          bValue = bItems;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sorting.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sorting.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  const handleRowClick = (checklist) => {
    handleEdit(checklist);
  };

  const fetchPresetChecklists = async () => {
    try {
      const response = await axios.get('/api/preset-checklists');
      setPresetChecklists(response.data.data);
    } catch (error) {
      console.error('Error fetching preset checklists:', error);
      setError(t('presets:failedFetchChecklists'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        items: [...prev.items, newItem.trim()]
      }));
      setNewItem('');
    }
  };

  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError(t('presets:nameRequired'));
      return;
    }

    if (formData.items.length === 0) {
      setError(t('presets:itemsRequired'));
      return;
    }

    try {
      if (editingChecklist) {
        await axios.put(`/api/preset-checklists/${editingChecklist.id}`, formData);
      } else {
        await axios.post('/api/preset-checklists', formData);
      }
      handleCloseModal();
      fetchPresetChecklists();
    } catch (error) {
      setError(
        formatError(error)
          || (editingChecklist
            ? t('presets:failedUpdateChecklist')
            : t('presets:failedCreateChecklist'))
      );
    }
  };

  const handleEdit = (checklist) => {
    setEditingChecklist(checklist);
    setFormData({
      name: checklist.name || '',
      items: Array.isArray(checklist.items) ? [...checklist.items] : []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('presets:confirmDeleteChecklist'), { danger: true }))) {
      return false;
    }
    try {
      await axios.delete(`/api/preset-checklists/${id}`);
      fetchPresetChecklists();
      return true;
    } catch (error) {
      setError(formatError(error) || t('presets:failedDeleteChecklist'));
      return false;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingChecklist(null);
    setFormData({ name: '', items: [] });
    setNewItem('');
    setError('');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedChecklists = sortChecklists(presetChecklists);

  return (
    <div className="preset-checklists">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('presets:checklistsPageTitle')}</h1>
          <div className="page-header-actions">
            <ColumnCustomizer
              columnLabels={checklistColumnLabels}
              visibleColumns={visibleColumns}
              onChange={(key, checked) => setVisibleColumns((prev) => ({ ...prev, [key]: checked }))}
            />
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={() => setShowModal(true)}>
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('presets:createChecklist')}
              </button>
            )}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {sortedChecklists.length > 0 ? (
          <>
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    {visibleColumns.name && renderSortableHeader('name', checklistColumnLabels.name)}
                    {visibleColumns.items && renderSortableHeader('items', checklistColumnLabels.items)}
                  </tr>
                </thead>
                <tbody>
                  {sortedChecklists.map((checklist) => {
                    const itemCount = Array.isArray(checklist.items) ? checklist.items.length : 0;
                    return (
                      <tr
                        key={checklist.id}
                        className="tasks-list-table-row"
                        onClick={() => handleRowClick(checklist)}
                      >
                        {visibleColumns.name && (
                          <td className="tasks-list-table-title">{checklist.name}</td>
                        )}
                        {visibleColumns.items && (
                          <td>{t('presets:itemCount', { count: itemCount })}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {sortedChecklists.map((checklist) => {
                const itemCount = Array.isArray(checklist.items) ? checklist.items.length : 0;
                return (
                  <article
                    key={checklist.id}
                    className="tasks-redesign-card"
                    onClick={() => handleRowClick(checklist)}
                  >
                    <div className="tasks-redesign-card-body">
                      <div className="tasks-redesign-card-top">
                        <h3 className="tasks-redesign-card-title">{checklist.name}</h3>
                      </div>
                      <div className="tasks-redesign-card-meta">
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">checklist</span>
                          {t('presets:itemCount', { count: itemCount })}
                        </span>
                      </div>
                      {(user?.role === 'super_admin' || user?.role === 'admin') && (
                        <div className="tasks-redesign-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(checklist.id)}
                          >
                            {t('common:delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          <EmptyState
            icon="checklist"
            title={t('presets:noChecklistsTitle')}
            description={t('presets:noChecklistsDescription')}
            actionLabel={(user?.role === 'super_admin' || user?.role === 'admin') ? t('presets:createChecklist') : undefined}
            onAction={(user?.role === 'super_admin' || user?.role === 'admin') ? () => setShowModal(true) : undefined}
          />
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingChecklist ? t('presets:editChecklist') : t('presets:createChecklist')}
        >
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('common:name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('presets:checklistItemsLabel')}</label>
                  <div className="checklist-editor">
                    {formData.items.map((item, index) => (
                      <div key={index} className="checklist-item">
                        <span>{item}</span>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveItem(index)}
                        >
                          {t('tasks:removeChecklistItem')}
                        </button>
                      </div>
                    ))}
                    <div className="add-checklist-item">
                      <input
                        type="text"
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        placeholder={t('tasks:placeholderChecklistItem')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddItem();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleAddItem}
                      >
                        {t('tasks:addChecklistItem')}
                      </button>
                    </div>
                  </div>
                </div>
                {error && <div className="error">{error}</div>}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingChecklist ? t('common:update') : t('common:create')}
                  </button>
                  {editingChecklist && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={async () => {
                        if (await handleDelete(editingChecklist.id)) {
                          handleCloseModal();
                        }
                      }}
                    >
                      {t('common:delete')}
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default PresetChecklists;
